import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import '../styles/LoginSignup.css';

// Loader Component
const Loader = () => (
  <div className="loader-overlay">
    <div className="loader" />
    <style jsx>{`
      .loader-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(250, 247, 237, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
      }
      .loader {
        border: 4px solid #E5E7EB;
        border-top: 4px solid #B45309;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function ResetPassword() {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!token || !email) {
      newErrors.form = 'Invalid or missing reset link';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          token,
          password: formData.password,
        }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        // Fetch user data after password reset
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          credentials: 'include',
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        navigate('/'); // Redirect to home or login page
      } else {
        setErrors({ ...errors, form: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, form: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="login-signup-main-container">
      {isLoading && <Loader />}
      <section className="login-hero-section-container">
        <div className="login-hero-content-container">
          <h1 className="login-hero-title">Reset Password</h1>
          <p className="login-hero-subtitle">
            Enter your new password to reset your Beaver Tours account.
          </p>
        </div>
      </section>
      <section className="login-signup-section-container">
        <div className="login-section-container">
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group password-group">
              <label htmlFor="password">New Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword.password ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Your New Password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('password')}
                >
                  {showPassword.password ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <span className="error">{errors.password}</span>}
            </div>
            <div className="form-group password-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-container">
                <input
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm Password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                >
                  {showPassword.confirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="error">{errors.confirmPassword}</span>
              )}
            </div>
            {errors.form && <span className="error">{errors.form}</span>}
            <button type="submit" className="form-submit-button" disabled={isLoading}>
              Reset Password
            </button>
            <div className="form-links">
              <button
                type="button"
                className="login-link"
                onClick={() => navigate('/login')}
                disabled={isLoading}
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </section>
      <style jsx>{`
        .password-group {
          position: relative;
        }
        .password-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        .password-input-container input {
          width: 100%;
          padding-right: 40px; /* Space for the eye button */
        }
        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          padding: 0;
          outline: none;
        }
        .login-link {
          font-size: 14px;
          color: #B45309;
          text-decoration: underline;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-top: 10px;
          display: inline-block;
        }
        .login-link:disabled {
          color: #6B4E31;
          cursor: not-allowed;
        }
      `}</style>
    </main>
  );
}

export default ResetPassword;